import classNames from 'classnames';
import type { ComponentProps, HTMLAttributes } from 'react';
type BetterInputAdditionalProps = {
  'aria-touched'?: boolean;
};

type InputType = 'input' | 'textarea';

type TextAreaInputProps = ComponentProps<'textarea'> &
  BetterInputAdditionalProps;
type NormalInputProps = ComponentProps<'input'> & BetterInputAdditionalProps;
export type BetterInputProps<T extends InputType | undefined = InputType> = {
  inputLabel?: string;
  inputType?: T;
  inputProps?: T extends 'textarea' ? TextAreaInputProps : NormalInputProps;
  leftComponent?: () => React.ReactNode;
  labelProps?: HTMLAttributes<HTMLHeadingElement>;
  formik?: any;
  rightComponent?: () => React.ReactNode;
  customValidator?: (val?: string | number | readonly string[]) => boolean;
};

/**
 * Renders a custom input component with additional features like left and right components, custom validation, and label support.
 *
 * @param inputLabel - The label for the input field.
 * @param inputType - The type of input field ('normal' | 'multiline').
 * @param labelProps - Additional props for the label element.
 * @param inputProps - Additional props for the input element.
 * @param leftComponent - Render prop for the content on the left side of the input field. Invoked during render, so inline arrows do not remount their subtree.
 * @param rightComponent - Render prop for the content on the right side of the input field.
 * @param customValidator - A custom validation function for the input value.
 *
 * @returns JSX.Element - The rendered custom input component.
 */
export const BetterInput = ({
  inputLabel,
  inputType = 'input',
  labelProps = {},
  inputProps = {},
  formik,
  leftComponent,
  rightComponent,
}: BetterInputProps) => {
  const fieldName = inputProps?.id ?? '';
  const defaultInputProps = {
    className: classNames(
      'ui-text-sm ui-border-none ui-m-0 ui-border-none focus:!ui-outline-none focus:!ui-outline-none ui-px-3 ui-py-2 ui-flex-1 placeholder:!ui-text-slate-400 ui-resize-none ',
      !leftComponent && !rightComponent
        ? 'ui-rounded-md ui-border-gray-300'
        : '',
      leftComponent ? 'ui-rounded-r-md' : '',
      rightComponent ? 'ui-rounded-l-md' : '',
      leftComponent && rightComponent ? '!ui-rounded-none' : ''
      // errorMessage && fieldTouched ? 'ui-outline' : ''
    ),
    value: formik?.values?.[fieldName],
    disabled: formik?.isSubmitting,
    'aria-errormessage': formik?.errors?.[fieldName],
    'aria-touched': formik?.touched?.[fieldName],
    onBlur: formik?.handleBlur,
    onChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      // Forms built on this input run with Formik's `validateOnChange: false`,
      // so `handleChange` updates the value without re-running validation and
      // the error raised by the previous blur/submit stays on screen (e.g.
      // "Message is required" next to a filled-in message). Drop the edited
      // field's error first; blur and submit re-validate the new value.
      if (formik?.errors?.[fieldName] != null) {
        formik.setFieldError(fieldName, undefined);
      }
      formik?.handleChange(event);
    },
  };

  const rawError =
    inputProps['aria-errormessage'] ??
    defaultInputProps['aria-errormessage'] ??
    '';
  const errorMessage = Array.isArray(rawError)
    ? [...new Set(rawError)].join(', ')
    : rawError;

  const fieldTouched =
    inputProps['aria-touched'] ?? defaultInputProps['aria-touched'] ?? '';
  return (
    <div className="ui-flex ui-flex-col ui-my-2 ui-w-full ui-rounded-none">
      {inputLabel && (
        <div className="ui-text-xs ui-px-1 ui-font-semibold" {...labelProps}>
          {inputLabel}
        </div>
      )}
      <div
        className={classNames(
          'ui-bg-gray-100 ui-rounded-md ui-mt-1 ui-flex ui-flex-row ui-items-center ui-justify-between ui-outline-inherit focus:ui-outline focus:ui-outline-blue-600 ui-border-solid ui-border',
          errorMessage && fieldTouched
            ? 'ui-border-red-600'
            : 'ui-border-gray-300',
          leftComponent ? 'ui-rounded-r-md' : '',
          rightComponent ? 'ui-rounded-l-md' : ''
        )}
      >
        {/* Icon left */}
        {leftComponent?.()}
        {/* Input */}
        {inputType === 'textarea' ? (
          <textarea
            {...defaultInputProps}
            {...(inputProps as TextAreaInputProps)}
          />
        ) : (
          <input
            type="text"
            {...defaultInputProps}
            {...(inputProps as NormalInputProps)}
          />
        )}
        {rightComponent?.()}
        {/* Icon right */}
      </div>
      {((errorMessage && fieldTouched) || inputProps.maxLength) && (
        <div className="ui-grid ui-grid-cols-[1fr_auto] ui-items-center ui-mt-0.5">
          {(errorMessage || fieldTouched) && (
            <div className={'ui-pl-1 ui-text-red-600 ui-text-xs'}>
              {fieldTouched ? errorMessage : ''}
            </div>
          )}

          {inputProps.maxLength && (
            <div className="ui-text-right ui-text-[10px] ui-text-gray-700 ui-pr-2">
              {`${(inputProps?.value ?? defaultInputProps?.value)?.length}/${inputProps.maxLength}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
